<script setup lang="ts">
/**
 * Черновик крупного действия (макеты 8-3, 8-3a, 8-4).
 *
 * Один блок на все крупные действия, а не свой экран на каждое: человек читает
 * «что сейчас произойдёт» в одном и том же месте разговора, и решение всегда
 * под одной и той же парой кнопок. Ход на сервере уже стоит — до нажатия
 * ничего не исполняется.
 *
 * Показывается ровно то, с чем инструмент позовут: пачка задач — таблицей,
 * переписывание текста — сравнением с тем, что в задаче сейчас. Незнакомый
 * инструмент доезжает сырыми аргументами: показать нечем, но подтвердить его
 * человек всё равно обязан, иначе разговор встанет насмерть.
 */
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { AppButton, AppSelect, AppTextField } from '@vinprop/design-system'
import { Trash2 } from 'lucide-vue-next'

import type { V1IssueType, V1Priority } from '@/api/clients/tracker-issue'
import { getIssueByKey } from '@/features/issue/api'
import { formatDueDate } from '@/features/issues/format'
import IssueTypeIcon from '@/features/issues/components/IssueTypeIcon.vue'
import { findSimilarIssues, type SimilarIssue } from '@/features/assistant/api'
import { usePlanStore } from '@/features/plan/store'
import type { PanelDraft } from '@/stores/assistant'

import DraftDiff from './DraftDiff.vue'
import { applyEdits, compressRanges, describeDraft, diffHunks, type DraftIssue } from './draft'
import { formatDetails } from './tools'

const props = defineProps<{ item: PanelDraft; busy: boolean }>()

const emit = defineEmits<{ decide: [approved: boolean, args?: unknown] }>()

const { t, locale } = useI18n()
const plan = usePlanStore()

const view = computed(() => describeDraft(props.item.name, props.item.args))

const isPending = computed(() => props.item.state === 'pending')

/**
 * Правки человека до подтверждения (DEV-1562).
 *
 * Копия, а не правка аргументов на месте: пока решение не принято, вызов на
 * сервере стоит с тем, что предложила модель, и отклонённый черновик обязан
 * остаться в разговоре её словами, а не наполовину переписанным.
 */
const edits = reactive<DraftIssue[]>([])
const removed = reactive(new Set<number>())

watch(
  view,
  (next) => {
    edits.splice(
      0,
      edits.length,
      ...(next.kind === 'issues' ? next.issues.map((issue) => ({ ...issue })) : []),
    )
    removed.clear()
  },
  { immediate: true },
)

/** Осталась ли хоть одна строка: пустую пачку подтверждать нечем. */
const remaining = computed(() => edits.filter((_, index) => !removed.has(index)))

const isChanged = computed(() => {
  if (view.value.kind !== 'issues') return false

  if (removed.size > 0) return true

  return view.value.issues.some((issue, index) => {
    const edit = edits[index]

    return (
      !edit ||
      edit.title !== issue.title ||
      edit.type !== issue.type ||
      edit.priority !== issue.priority
    )
  })
})

const TYPES: V1IssueType[] = [
  'ISSUE_TYPE_TASK',
  'ISSUE_TYPE_BUG',
  'ISSUE_TYPE_STORY',
  'ISSUE_TYPE_EPIC',
  'ISSUE_TYPE_SUBTASK',
]

const PRIORITIES: V1Priority[] = [
  'PRIORITY_HIGHEST',
  'PRIORITY_HIGH',
  'PRIORITY_MEDIUM',
  'PRIORITY_LOW',
  'PRIORITY_LOWEST',
]

const typeOptions = computed(() =>
  TYPES.map((type) => ({
    value: type.replace('ISSUE_TYPE_', '').toLowerCase(),
    label: t(`issues.types.${type}`),
  })),
)

const priorityOptions = computed(() =>
  PRIORITIES.map((priority) => ({
    value: priority.replace('PRIORITY_', '').toLowerCase(),
    label: t(`issues.priorities.${priority}`),
  })),
)

/**
 * Похожие задачи (DEV-1561).
 *
 * Показываются до создания и ничего не запрещают: «всё равно создать» —
 * законный ответ, дубль иногда заводят осознанно. Ищем только у одиночной
 * задачи: на пачке это был бы запрос на строку, а строк бывает полсотни.
 */
const similar = ref<SimilarIssue[]>([])

watch(
  () =>
    view.value.kind === 'issues' && !view.value.batch && isPending.value
      ? view.value.issues[0]?.title
      : '',
  async (title) => {
    similar.value = title ? await findSimilarIssues(title) : []
  },
  { immediate: true },
)

const decide = (approved: boolean) => {
  if (!approved || !isChanged.value) {
    emit('decide', approved)

    return
  }

  emit('decide', true, applyEdits(props.item.name, props.item.args, edits, removed))
}

/** Значения инструментов — строчные: `task`, `medium`. Словарь — по enum'ам. */
const issueType = (type: string) => `ISSUE_TYPE_${type.toUpperCase()}` as V1IssueType
const priorityKey = (priority: string) => `PRIORITY_${priority.toUpperCase()}` as V1Priority

/**
 * Как выглядит задача сейчас.
 *
 * Дозапрос, а не поле события: в аргументах вызова лежит только новый текст, а
 * без старого сравнение превращается в «весь текст добавлен» — по нему не
 * увидеть, что именно модель переписала.
 */
const before = ref<{ title: string; description_md: string } | null>(null)

watch(
  () => (view.value.kind === 'fields' ? view.value.issueKey : ''),
  async (key) => {
    if (!key) return

    try {
      const issue = await getIssueByKey(key)

      before.value = issue
        ? { title: issue.title, description_md: issue.descriptionMd }
        : { title: '', description_md: '' }
    } catch {
      // Задача не прочиталась — сравнивать не с чем, и черновик показывает
      // только новый текст. Кнопки при этом работают: решение человека от
      // доступности старого текста не зависит.
      before.value = { title: '', description_md: '' }
    }
  },
  { immediate: true },
)

const fields = computed(() => {
  if (view.value.kind !== 'fields') return []

  return view.value.fields.map((field) => ({
    name: field.name,
    hunks: diffHunks(before.value?.[field.name] ?? '', field.after),
  }))
})

/**
 * Чем занята задача — одной фразой (макет 9-4).
 *
 * Из плана, а не из вызова: инструмент присылает голый порядок ключей, и сроки
 * с колонками панель берёт там же, где их берёт сам план на экране. Без этой
 * фразы черновик — столбик ключей, по которому не проверить, права ли модель.
 */
const factsOf = (key: string): string => {
  const item = plan.items.find((row) => row.key === key)

  if (!item) return ''

  const facts: string[] = []
  const due = item.dueDate ? new Date(item.dueDate) : null
  const now = new Date()

  if (due && !Number.isNaN(due.getTime())) {
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()

    facts.push(
      due.getTime() < today
        ? t('plan.row.overdue')
        : t('plan.draft.due', { date: formatDueDate(item.dueDate, locale.value) }),
    )
  }

  if (item.statusCategory === 'STATUS_CATEGORY_IN_PROGRESS' && item.columnName) {
    facts.push(item.columnName.toLocaleLowerCase(locale.value))
  }

  return facts.join(', ')
}

interface MovedRow {
  key: string
  position: number
  /** Откуда переехала; ноль — задачи в плане ещё нет, модель её добавляет */
  from: number
  facts: string
}

/**
 * Строки нового порядка: только те, что переехали.
 *
 * Неподвижные не показываются вовсе — их место в подписи под таблицей. Иначе
 * перестановка трёх задач в плане из тридцати выглядит как правка всего плана,
 * и найти в ней сами перестановки нечем.
 */
const order = computed(() => {
  if (view.value.kind !== 'order') return { moved: [] as MovedRow[], kept: '' }

  const was = new Map(plan.items.map((item, index) => [item.key, index + 1]))
  const moved: MovedRow[] = []
  const kept: number[] = []

  view.value.keys.forEach((key, index) => {
    const position = index + 1
    const from = was.get(key) ?? 0

    if (from === position) {
      kept.push(position)

      return
    }

    moved.push({ key, position, from, facts: factsOf(key) })
  })

  return { moved, kept: compressRanges(kept) }
})

/** Подпись слева: «черновик · 6 задач», «черновик · описание». */
const caption = computed(() => {
  const badge = t('assistant.draft.badge')

  if (view.value.kind === 'issues') {
    return `${badge} · ${t('assistant.tool.issues', view.value.issues.length)}`
  }

  if (view.value.kind === 'fields') {
    return `${badge} · ${view.value.fields.map((field) => t(`assistant.draft.field.${field.name}`)).join(', ')}`
  }

  if (view.value.kind === 'order') return `${badge} · ${t('plan.draft.order')}`

  return `${badge} · ${props.item.name}`
})

/** Подпись справа: куда ляжет пачка или какую задачу правят. */
const subject = computed(() => {
  if (view.value.kind === 'issues') {
    return view.value.epicKey
      ? t('assistant.draft.inEpic', { key: view.value.epicKey })
      : view.value.projectKey
  }

  return view.value.kind === 'fields' ? view.value.issueKey : ''
})

const confirmLabel = computed(() => {
  // Счёт по оставшимся строкам: убранные из пачки в создание не идут (DEV-1562).
  if (view.value.kind === 'issues') return t('assistant.draft.create', remaining.value.length)

  if (view.value.kind === 'order') return t('plan.draft.apply')

  return t('assistant.draft.apply')
})
</script>

<template>
  <section class="draft" :class="{ 'draft--settled': !isPending }">
    <header class="draft__head">
      <span class="draft__caption">{{ caption }}</span>
      <span
        v-if="subject"
        class="draft__subject"
        :class="{ 'draft__subject--key': view.kind === 'fields' }"
      >
        {{ subject }}
      </span>
    </header>

    <!--
      Таблица пачки: по строке на задачу, как в списке задач. Пока решение не
      принято, строки правятся прямо здесь (DEV-1562) — переписывать заголовок
      через новый вопрос ассистенту дороже, чем поправить его на месте.
    -->
    <ul v-if="view.kind === 'issues'" class="draft__rows">
      <li
        v-for="(issue, index) in view.issues"
        v-show="!removed.has(index)"
        :key="index"
        class="draft__row"
        :class="{ 'draft__row--edit': isPending }"
      >
        <template v-if="isPending && edits[index]">
          <AppSelect
            v-model="edits[index].type"
            :options="typeOptions"
            size="small"
            class="draft__type"
            :aria-label="t('issues.create.type')"
          />

          <AppTextField
            v-model="edits[index].title"
            size="small"
            class="draft__input"
            :aria-label="t('assistant.draft.rowTitle')"
          />

          <AppSelect
            v-model="edits[index].priority"
            :options="priorityOptions"
            size="small"
            class="draft__priority-select"
            :aria-label="t('issues.create.priority')"
          />

          <button
            v-if="view.batch"
            type="button"
            class="draft__drop"
            :title="t('assistant.draft.dropRow')"
            :aria-label="t('assistant.draft.dropRow')"
            @click="removed.add(index)"
          >
            <Trash2 :size="14" />
          </button>
        </template>

        <template v-else>
          <IssueTypeIcon :type="issueType(issue.type)" />

          <span class="draft__title">{{ issue.title }}</span>
          <span class="draft__priority">{{
            t(`issues.priorities.${priorityKey(issue.priority)}`)
          }}</span>
        </template>
      </li>
    </ul>

    <!-- Новый порядок плана: номер, откуда переехала, ключ и чем занята. -->
    <ul v-else-if="view.kind === 'order'" class="draft__rows">
      <li v-for="row in order.moved" :key="row.key" class="draft__row">
        <span class="draft__position">{{ row.position }}</span>
        <span v-if="row.from" class="draft__from">← {{ row.from }}</span>
        <span v-else class="draft__from">+</span>

        <RouterLink class="draft__key" :to="`/issue/${row.key}`">{{ row.key }}</RouterLink>

        <span class="draft__facts">{{ row.facts }}</span>
      </li>

      <li v-if="order.kept" class="draft__kept">
        {{ t('plan.draft.kept', { positions: order.kept }) }}
      </li>
    </ul>

    <template v-else-if="view.kind === 'fields'">
      <DraftDiff v-for="field in fields" :key="field.name" :hunks="field.hunks" />
    </template>

    <pre v-else class="draft__raw">{{ formatDetails(item.args) }}</pre>

    <!--
      Похожие задачи — предупреждение, а не запрет (DEV-1561): создать всё
      равно можно, кнопка рядом и не гаснет.
    -->
    <p v-if="similar.length && isPending" class="draft__similar">
      {{ t('assistant.draft.similar') }}
      <RouterLink
        v-for="issue in similar"
        :key="issue.key"
        class="draft__similar-key"
        :to="`/issue/${issue.key}`"
        :title="issue.title"
      >
        {{ issue.key }}
      </RouterLink>
    </p>

    <footer class="draft__foot">
      <template v-if="isPending">
        <AppButton size="small" variant="secondary" :disabled="busy" @click="decide(false)">
          {{ t('assistant.draft.decline') }}
        </AppButton>

        <AppButton
          size="small"
          :disabled="busy || (view.kind === 'issues' && !remaining.length)"
          @click="decide(true)"
        >
          {{ confirmLabel }}
        </AppButton>
      </template>

      <span v-else class="draft__settled">
        {{
          item.state === 'applied' ? t('assistant.draft.applied') : t('assistant.draft.declined')
        }}
      </span>
    </footer>
  </section>
</template>

<style scoped>
.draft {
  container-type: inline-size;
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-bg-app);
}

/* Решённый черновик остаётся в разговоре, но не тянет на себя внимание. */
.draft--settled {
  opacity: 0.7;
}

.draft__head {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 10px;
  border-bottom: 1px solid var(--color-border);
}

.draft__caption {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  text-overflow: ellipsis;
  text-transform: uppercase;
}

.draft__subject {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.draft__subject--key {
  font-family: var(--font-family-mono);
  font-weight: var(--font-weight-medium);
}

.draft__rows {
  margin: 0;
  padding: 0;
  list-style: none;
}

.draft__row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 6px 10px;
}

.draft__row + .draft__row {
  border-top: 1px solid var(--color-border);
}

.draft__title {
  flex: 1;
  min-width: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-body-sm);
  overflow-wrap: anywhere;
}

.draft__priority {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.draft__position,
.draft__from {
  flex-shrink: 0;
  font-family: var(--font-family-mono);
  font-size: var(--font-size-body-sm);
  line-height: 20px;
}

.draft__position {
  color: var(--color-text-primary);
}

/* Откуда переехала строка — цветом плана: это и есть содержание черновика */
.draft__from {
  color: var(--color-primary);
}

.draft__key {
  flex-shrink: 0;
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  text-decoration: none;
}

.draft__facts {
  flex: 1;
  min-width: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
  overflow-wrap: anywhere;
}

.draft__kept {
  padding: 4px 10px 8px;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

/* Узкая панель (макет 8-3a): приоритет уступает место заголовку задачи. */
@container (max-width: 320px) {
  .draft__priority {
    display: none;
  }
}

/* Строка в правке выше обычной: в ней живут поля, а не текст. */
.draft__row--edit {
  gap: 6px;
  min-height: 48px;
}

.draft__type {
  flex-shrink: 0;
  width: 104px;
}

.draft__input {
  flex: 1;
  min-width: 0;
}

.draft__priority-select {
  flex-shrink: 0;
  width: 104px;
}

.draft__drop {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--color-text-muted);
  cursor: pointer;
}

.draft__drop:hover {
  background: var(--color-bg-muted);
  color: var(--color-danger-strong);
}

.draft__similar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  padding: 8px 10px;
  border-top: 1px solid var(--color-border);
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.draft__similar-key {
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-weight: var(--font-weight-medium);
  text-decoration: none;
}

.draft__similar-key:hover {
  color: var(--color-text-primary);
}

/* Узкая панель (макет 8-3a): приоритет уступает место заголовку задачи. */
@container (max-width: 320px) {
  .draft__priority,
  .draft__priority-select {
    display: none;
  }
}

.draft__raw {
  max-height: 200px;
  margin: 0;
  padding: 8px 10px;
  overflow: auto;
  color: var(--color-text-secondary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-caption);
  line-height: 16px;
}

.draft__foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px;
  border-top: 1px solid var(--color-border);
  background: var(--color-bg-muted);
}

.draft__settled {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}
</style>
